import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Filter, RefreshCw, ShieldAlert, FileText, Users, Settings as SettingsIcon, Ticket, Mail, MailX, ChevronDown } from "lucide-react";

const PAGE_SIZE = 50;

const ACTION_META = {
  ticket_update: { label: "Ticket update", icon: Ticket, tint: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  ticket_create: { label: "Ticket created", icon: Ticket, tint: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ticket_delete: { label: "Ticket deleted", icon: Ticket, tint: "bg-rose-50 text-rose-700 border-rose-200" },
  permission_update: { label: "Permission updated", icon: ShieldAlert, tint: "bg-amber-50 text-amber-700 border-amber-200" },
  user_invite: { label: "User invited", icon: Users, tint: "bg-sky-50 text-sky-700 border-sky-200" },
  user_delete: { label: "User removed", icon: Users, tint: "bg-rose-50 text-rose-700 border-rose-200" },
  email_sent: { label: "Email sent", icon: Mail, tint: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  email_failed: { label: "Email failed", icon: MailX, tint: "bg-rose-50 text-rose-700 border-rose-200" },
};

const ENTITY_OPTIONS = [
  { value: "all", label: "All entities" },
  { value: "ticket", label: "Tickets" },
  { value: "permission", label: "Permissions" },
  { value: "user", label: "Users" },
  { value: "email", label: "Emails" },
];

const prettify = (a = "") => a.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const metaFor = (action) =>
  ACTION_META[action] || { label: prettify(action), icon: SettingsIcon, tint: "bg-slate-100 text-slate-600 border-slate-200" };

const initials = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "??";

const formatVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

export default function ActivityLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");

  // Filters
  const [entity, setEntity] = useState("all");
  const [action, setAction] = useState("all");
  const [emailQ, setEmailQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [actionOptions, setActionOptions] = useState([]);

  const buildParams = (skip) => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(skip) });
    if (entity !== "all") params.set("entity_type", entity);
    if (action !== "all") params.set("action", action);
    if (emailQ.trim()) params.set("user_email", emailQ.trim());
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    return params;
  };

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get(`/audit-log?${buildParams(0).toString()}`);
      const data = r.data || {};
      setRows(data.items || []);
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unable to load activity log");
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await api.get(`/audit-log?${buildParams(rows.length).toString()}`);
      const data = r.data || {};
      setRows((prev) => [...prev, ...(data.items || [])]);
      setHasMore(!!data.has_more);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unable to load more");
    } finally {
      setLoadingMore(false);
    }
  };

  // initial + when dropdown filters change
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entity, action]);

  // distinct action list for the dropdown
  useEffect(() => {
    api.get("/audit-log/actions").then((r) => setActionOptions(r.data || [])).catch(() => {});
  }, []);

  const resetFilters = () => {
    setEntity("all"); setAction("all"); setEmailQ(""); setDateFrom(""); setDateTo("");
  };

  const grouped = useMemo(() => {
    const out = {};
    rows.forEach((r) => {
      const d = (r.created_at || "").slice(0, 10);
      (out[d] = out[d] || []).push(r);
    });
    return out;
  }, [rows]);

  return (
    <div data-testid="activity-log">
      {/* Filters */}
      <Card className="border-slate-200 mb-4">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="min-w-[160px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Entity</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger className="mt-1" data-testid="activity-filter-entity"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ENTITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Action</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="mt-1" data-testid="activity-filter-action"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="all">All actions</SelectItem>
                {actionOptions.map((a) => <SelectItem key={a} value={a}>{prettify(a)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[150px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">From date</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" data-testid="activity-filter-date-from" />
          </div>
          <div className="min-w-[150px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">To date</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" data-testid="activity-filter-date-to" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">User email</Label>
            <Input
              placeholder="ahmed.support@kippu.demo"
              value={emailQ}
              onChange={(e) => setEmailQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              className="mt-1"
              data-testid="activity-filter-email"
            />
          </div>
          <Button variant="outline" onClick={load} className="border-slate-200" data-testid="activity-apply-btn">
            <Filter className="w-3.5 h-3.5 mr-1.5" /> Apply
          </Button>
          <Button variant="ghost" onClick={() => { resetFilters(); }} className="text-slate-500" data-testid="activity-reset-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reset
          </Button>
        </CardContent>
      </Card>

      {/* Body */}
      {loading ? (
        <div className="text-center py-16 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      ) : err ? (
        <Card className="border-rose-100 bg-rose-50/60">
          <CardContent className="p-5 text-sm text-rose-700">{err}</CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-10 text-center text-slate-400 text-sm">
            <FileText className="w-6 h-6 mx-auto mb-2 text-slate-300" />
            No activity yet for these filters.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-[11px] text-slate-400" data-testid="activity-count">
              Showing {rows.length} of {total} entries
            </span>
          </div>
          <div className="space-y-6" data-testid="activity-timeline">
            {Object.entries(grouped).map(([day, items]) => (
              <div key={day}>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold mb-2 pl-1">
                  {new Date(day).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}
                </div>
                <Card className="border-slate-200">
                  <CardContent className="p-0">
                    <ul className="divide-y divide-slate-100">
                      {items.map((r) => {
                        const meta = metaFor(r.action);
                        const Icon = meta.icon;
                        const t = new Date(r.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
                        const changedKeys = Object.keys(r.after || {});
                        return (
                          <li key={r.id} className="px-4 py-3 hover:bg-slate-50/60" data-testid={`activity-row-${r.id}`}>
                            <div className="flex items-start gap-3">
                              <div className="w-9 h-9 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center text-[11px] font-semibold border border-indigo-100 shrink-0">
                                {initials(r.user_name)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 text-sm">
                                  <span className="font-semibold text-slate-800">{r.user_name || r.user_email}</span>
                                  <Badge variant="outline" className={`text-[10px] ${meta.tint}`}>
                                    <Icon className="w-3 h-3 mr-1" />{meta.label}
                                  </Badge>
                                  {r.entity_id && (
                                    <span className="text-[11px] text-slate-400 font-mono truncate max-w-[260px]">
                                      {r.entity_type}:{r.entity_id.slice(0, 8)}
                                    </span>
                                  )}
                                  <span className="ml-auto text-[11px] text-slate-400">{t}</span>
                                </div>
                                {changedKeys.length > 0 && (
                                  <div className="mt-2 rounded-md bg-slate-50 border border-slate-100 px-3 py-2 text-xs font-mono space-y-1">
                                    {changedKeys.filter((k) => k !== "updated_at").slice(0, 6).map((k) => (
                                      <div key={k} className="flex items-start gap-2 flex-wrap">
                                        <span className="text-slate-500">{k}</span>
                                        <span className="text-rose-600 line-through truncate max-w-[280px]">{formatVal(r.before?.[k])}</span>
                                        <span className="text-slate-300">→</span>
                                        <span className="text-emerald-700 truncate max-w-[280px]">{formatVal(r.after?.[k])}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
          {hasMore && (
            <div className="text-center mt-5">
              <Button variant="outline" onClick={loadMore} disabled={loadingMore} className="border-slate-200" data-testid="activity-load-more">
                {loadingMore ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ChevronDown className="w-3.5 h-3.5 mr-1.5" />}
                Load more
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
